import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-flexbox-grid';
import config from '../../config';
import TableRankingTeamItem from '../TableRankingTeamItem';
import EmptyState from '../EmptyState';
import { handleResponse, serializeQuery } from '../../utils/handleResponse';

const TableCalendarTeam = ({
  raceDay,
  raceEvent,
  setShowLoading,
  setShowSecondLoading,
  teamFavorites,
  saveTeamFavorite,
  deleteTeamFavorite,
  changeTeam,
  selectedGenderCategoryFilter,
  selectedCategoryFilter,
  searchText,
  favoritesEnabled,
}) => {
  const [startingTimes, setStartingTimes] = useState([]);
  const [startingTimesFavorite, setStartingTimesFavorite] = useState([]);

  const normalizeAgeCategory = (rawValue) => {
    const value = String(rawValue || '')
      .trim()
      .toUpperCase();
    if (!value) return null;
    if (
      value.includes('AGE_100_PLUS') ||
      value.includes('AGE_100+') ||
      value.includes('100+')
    ) {
      return '100+';
    }
    if (value.includes('AGE_99') || value === '99' || value.includes('<=100')) {
      return '99';
    }
    if (value === '100') return '100+';
    return null;
  };

  const getTeamAgeCategory = (schedule) => {
    const participants = schedule?.team?.participants || [];
    const participantCategory = participants
      .map((p) => normalizeAgeCategory(p?.category))
      .find(Boolean);

    if (participantCategory) return participantCategory;

    const teamCategory = normalizeAgeCategory(schedule?.team?.teamAgeCategory);
    if (teamCategory) return teamCategory;

    return null;
  };

  const applyTeamAgeFilter = (schedules) => {
    if (!selectedCategoryFilter) return schedules;
    return schedules.filter((schedule) => {
      const gender = String(schedule?.team?.gender || '').toUpperCase();
      // Para mujeres no aplica filtro de edad por categoría.
      if (gender === 'WOMEN') return true;
      const ageCategory = getTeamAgeCategory(schedule);
      // Estricto: si no hay categoría persistida, no pasa el filtro.
      return ageCategory === selectedCategoryFilter;
    });
  };

  const normalizeSearch = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const applySearchFilter = (schedules) => {
    const term = normalizeSearch(searchText).trim();
    if (!term) return schedules;
    return schedules.filter((schedule) => {
      const teamName = normalizeSearch(schedule?.team?.name);
      if (teamName.includes(term)) return true;
      const participants = schedule?.team?.participants || [];
      return participants.some((p) => {
        const fullName = normalizeSearch(`${p?.name || ''} ${p?.last_name || ''}`);
        return fullName.includes(term);
      });
    });
  };

  const getCalendar = async () => {
    setShowLoading(true);
    let athlete = null;
    try {
      athlete = JSON.parse(localStorage.getItem('userData') || 'null');
    } catch (e) {
      athlete = null;
    }
    const headers = athlete?.userToken
      ? {
          Authorization: `Bearer ${athlete.userToken}`,
          'Content-Type': 'application/json',
        }
      : undefined;

    const requestOptions = {
      method: 'GET',
      ...(headers ? { headers } : {}),
    };

    const params = {
      day: raceDay,
    };

    if (selectedGenderCategoryFilter) {
      if (!params['query']) {
        params['query'] = {};
      }
      params['query']['gender'] = selectedGenderCategoryFilter;
    }

    if (!params['query']) {
      params['query'] = {};
    }

    const url = new URL(
      `${config.tncApiUrl}/sport-events/${config.eventId}/schedules`
    );
    url.search = serializeQuery(params);

    const response = await fetch(url, requestOptions);
    setShowLoading(false);
    try {
      const newStartingTimes = await handleResponse(response);
      console.log('newStartingTimes', newStartingTimes);
      const schedules = (newStartingTimes?.data?.schedules || []).filter(
        (schedule) => !!schedule?.team
      );
      setStartingTimes(applyTeamAgeFilter(schedules));
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (!startingTimes.length || !teamFavorites.length) {
      setStartingTimesFavorite([]);
      return;
    }
    // IDs (userId) que el usuario marcó como favoritos
    const favIdSet = new Set(teamFavorites.map((f) => f.teamId));
    console.log('teamFavorites', teamFavorites);
    console.log('rakings', startingTimes);

    const favTimes = startingTimes.filter((st) => favIdSet.has(st?.team?.id));
    console.log('favTimes', favTimes);
    setStartingTimesFavorite(favTimes);
  }, [startingTimes, teamFavorites]);

  useEffect(() => {
    if (raceDay) {
      getCalendar();
    }
  }, [raceDay]);

  useEffect(() => {
    if (changeTeam) {
      getCalendar();
    }
  }, [changeTeam]);

  const visibleStartingTimes = applySearchFilter(startingTimes);
  const visibleStartingTimesFavorite = applySearchFilter(startingTimesFavorite);

  return (
    <Col xs={12}>
      <Row>
        {raceDay &&
          visibleStartingTimes.length > 0 &&
          visibleStartingTimesFavorite.map((startingTime, index) => {
            return (
              <TableRankingTeamItem
                userInfo={{ ranking: index + 1, ...startingTime }}
                key={startingTime._id}
                teamFavorites={teamFavorites}
                saveTeamFavorite={saveTeamFavorite}
                deleteTeamFavorite={deleteTeamFavorite}
                calendar
                darkMode
                overlapBadge
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
        {!raceDay || (visibleStartingTimes.length === 0 && <EmptyState />)}
        {raceDay &&
          visibleStartingTimes.map((startingTime, index) => {
            return (
              <TableRankingTeamItem
                userInfo={{ ranking: index + 1, ...startingTime }}
                key={startingTime._id}
                raceDay={raceDay}
                firstRaking={startingTimes[0]}
                teamFavorites={teamFavorites}
                saveTeamFavorite={saveTeamFavorite}
                deleteTeamFavorite={deleteTeamFavorite}
                calendar
                overlapBadge
                favoritesEnabled={favoritesEnabled}
              />
            );
          })}
      </Row>
    </Col>
  );
};

TableCalendarTeam.propTypes = {
  setShowLoading: PropTypes.func.isRequired,
  setShowSecondLoading: PropTypes.func.isRequired,
  raceDay: PropTypes.number.isRequired,
  raceEvent: PropTypes.object.isRequired,
  teamFavorites: PropTypes.array.isRequired,
  saveTeamFavorite: PropTypes.func.isRequired,
  deleteTeamFavorite: PropTypes.func.isRequired,
  selectedGenderCategoryFilter: PropTypes.string,
  selectedCategoryFilter: PropTypes.string,
  searchText: PropTypes.string,
  changeTeam: PropTypes.number,
  favoritesEnabled: PropTypes.bool,
};

TableCalendarTeam.defaultProps = {
  selectedGenderCategoryFilter: null,
  selectedCategoryFilter: null,
  searchText: '',
  changeTeam: null,
  favoritesEnabled: false,
};

export default TableCalendarTeam;
